import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactElement } from 'react';
import type { TabId, NavTab } from '../types.js';

export interface SideMenuProps {
  open: boolean;
  tabs: ReadonlyArray<NavTab>;
  active: TabId;
  onNavigate: (id: TabId) => void;
  onClose: () => void;
}

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
}

export function SideMenu({
  open,
  tabs,
  active,
  onNavigate,
  onClose,
}: SideMenuProps): ReactElement | null {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        onClose();
        return;
      }
      if (ev.key === 'Tab' && panelRef.current) {
        const focusables = getFocusable(panelRef.current);
        if (focusables.length === 0) {
          ev.preventDefault();
          panelRef.current.focus();
          return;
        }
        const first = focusables[0]!;
        const last = focusables[focusables.length - 1]!;
        const activeEl = document.activeElement as HTMLElement | null;
        if (ev.shiftKey && activeEl === first) {
          ev.preventDefault();
          last.focus();
        } else if (!ev.shiftKey && activeEl === last) {
          ev.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => {
      if (panelRef.current) {
        const focusables = getFocusable(panelRef.current);
        (focusables[0] ?? panelRef.current).focus();
      }
    }, 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function') {
        prev.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-40"
      data-testid="side-menu-root"
      data-open={open ? 'true' : 'false'}
    >
      <button
        type="button"
        aria-label="Close menu"
        onClick={onClose}
        className="absolute inset-0"
        style={{
          background: 'color-mix(in srgb, #000 50%, transparent)',
          border: 'none',
          cursor: 'pointer',
        }}
        data-testid="side-menu-backdrop"
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="absolute left-0 top-0 flex h-full w-72 max-w-[85%] flex-col border-r"
        style={{
          background: 'var(--bg-surface)',
          borderColor: 'var(--border)',
          boxShadow: '4px 0 12px rgba(0,0,0,0.15)',
          transform: 'translateX(0)',
          transition: 'transform 160ms ease-out',
        }}
        data-testid="side-menu-panel"
      >
        <div
          className="flex items-center justify-between border-b px-4 py-3"
          style={{ borderColor: 'var(--border)' }}
        >
          <h2
            id={titleId}
            className="flex items-center gap-2 text-lg font-semibold"
            style={{ color: 'var(--text-main)' }}
            data-testid="side-menu-title"
          >
            <span aria-hidden="true">👨‍🍳</span>
            <span>ChefCook</span>
          </h2>
          <button
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            className="flex min-h-[40px] min-w-[40px] items-center justify-center rounded-md"
            style={{
              color: 'var(--text-main)',
              background: 'transparent',
            }}
            data-testid="side-menu-close"
          >
            <span aria-hidden="true">✕</span>
          </button>
        </div>
        <nav
          aria-label="Primary"
          className="flex flex-col p-2"
          data-testid="side-menu-nav"
        >
          {tabs.map((tab) => {
            const isActive = active === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                role="link"
                aria-current={isActive ? 'page' : undefined}
                aria-label={tab.label}
                data-tab={tab.id}
                data-active={isActive ? 'true' : 'false'}
                data-testid={`side-menu-tab-${tab.id}`}
                onClick={() => {
                  onNavigate(tab.id);
                  onClose();
                }}
                className="flex min-h-[44px] items-center gap-3 rounded-md px-3 py-2 text-left text-sm"
                style={{
                  background: isActive
                    ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
                    : 'transparent',
                  color: isActive ? 'var(--accent)' : 'var(--text-main)',
                  fontWeight: isActive ? 600 : 400,
                }}
              >
                <span aria-hidden="true" className="text-xl leading-none">
                  {tab.icon}
                </span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>
    </div>,
    document.body,
  );
}
